import React, { Component } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker, SingleDatePicker, isInclusivelyBeforeDay } from 'react-dates';
import {
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  Line,
  YAxis,
  Legend,
  Tooltip,
  Label,
} from 'recharts';
import classNames from 'classnames';
import moment from 'moment';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import {
  formatNumber,
  formatNumberView,
  formatDateYear,
  formatDate,
  formatRequestDate,
} from '../../../../utils/helpers';
import SwitchGroup from '../../../../components/_molecules/SwitchGroup/SwitchGroup';
import {
  DASHBOARD_FILTERS_NAMES,
  TEXT_FIELD_STATUS,
  PARAMETERS,
  SUCCESS,
} from '../../../../utils/constants';
import ChartPlaceholder from './components/ChartPlaceholder/ChartPlaceholder';
import ChartFilters from './components/ChartFilters/ChartFilters';

import './ChartPart.scss';
import CustomMultiSelect from '../../../../components/_molecules/CustomMultiSelect/CustomMultiSelect';

const switchGroupValues = [
  {
    label: 'Day',
    value: 'DAY',
  },
  {
    label: 'Week',
    value: 'WEEK',
  },
  {
    label: 'Month',
    value: 'MONTH',
  },
];

const switchDatePickerType = [
  {
    label: 'Date',
    value: 'DATE',
  },
  {
    label: 'Range',
    value: 'RANGE',
  },
];

const commonLabelStyle = {
  textAnchor: 'end',
  fill: '#8f9ea6',
  fontFamily: 'Nunito',
  fontSize: '12',
  fontWeight: '600',
};

const xLabelStyle = {
  transform: 'translateX(-22px) translateY(8px)',
};

const arrowIcon = require('./assets/arrow.svg');

class ChartPart extends Component {
  static propTypes = {
    getAppCampaigns: PropTypes.func.isRequired,
    appId: PropTypes.number,
    appCampaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isAdmin: PropTypes.bool,
    getChartReport: PropTypes.func.isRequired,
    getTableReport: PropTypes.func.isRequired,
    chartReport: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    getChartCompareReport: PropTypes.func.isRequired,
    chartCompareReport: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    resetCompare: PropTypes.func.isRequired,
    getExport: PropTypes.func.isRequired,
    users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    apps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    getAppsStatus: PropTypes.string.isRequired,
    hasMoreApps: PropTypes.bool.isRequired,
    pageApps: PropTypes.number.isRequired,
    getApps: PropTypes.func.isRequired,
    isOverview: PropTypes.bool,
    resetChartReport: PropTypes.func.isRequired,
    getCurrentDate: PropTypes.func.isRequired,
    updateDatePicker: PropTypes.func.isRequired,
  };
  static defaultProps = {
    isAdmin: false,
    isOverview: false,
    appId: 0,
  };

  state = {
    filters: {
      [DASHBOARD_FILTERS_NAMES.USER]: {
        value: '',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
      [DASHBOARD_FILTERS_NAMES.APP]: {
        value: '',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
      [DASHBOARD_FILTERS_NAMES.PLATFORM]: {
        value: '',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
      [DASHBOARD_FILTERS_NAMES.CAMPAIGN]: {
        value: '',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
      [DASHBOARD_FILTERS_NAMES.PARAMETER]: {
        value: 'CPI',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
      [DASHBOARD_FILTERS_NAMES.COMPARE]: {
        value: '',
        status: TEXT_FIELD_STATUS.INITIAL,
      },
    },
    periodSwitchValue: 'DAY',
    datePickerTypeSwitchValue: 'RANGE',
    datePicker: {
      startDate: moment().subtract(1, 'month'),
      endDate: moment(),
    },
    focusedDatePickerInput: null,
  };

  componentDidMount() {
    const {
      appId, getAppCampaigns, getChartReport, getTableReport, isOverview, getCurrentDate,
    } = this.props;
    if (appId) {
      getAppCampaigns({ appId });
      getChartReport(this.formatRequestParameters(appId));
      getTableReport(this.formatRequestParameters(appId));
      getCurrentDate(this.formatRequestParameters(appId));
    }
    if (isOverview) {
      getChartReport(this.formatRequestParameters());
    }
  }

  componentDidUpdate(prevProps) {
    const {
      appId,
      getAppCampaigns,
      getChartReport,
      getTableReport,
      getCurrentDate,
      resetCompare,
      isOverview,
    } = this.props;
    if (appId !== prevProps.appId && !Number.isNaN(appId)) {
      const filters = {
        ...this.state.filters,
      };
      const datePicker = {
        ...this.state.datePicker,
      };
      Object.keys(filters).forEach((filterName) => {
        if (filterName !== DASHBOARD_FILTERS_NAMES.PARAMETER) {
          filters[filterName].value = '';
          filters[filterName].status = TEXT_FIELD_STATUS.INITIAL;
        } else {
          filters[filterName].value = 'CPI';
          filters[filterName].status = TEXT_FIELD_STATUS.VALID;
        }
      });
      resetCompare();
      datePicker.startDate = moment().subtract(1, 'month');
      datePicker.endDate = moment();
      this.setState(
        () => ({ filters, periodSwitchValue: 'DAY', datePicker }),
        () => {
          if (!isOverview) {
            getAppCampaigns({ appId });
            getChartReport(this.formatRequestParameters(appId));
            getTableReport(this.formatRequestParameters(appId));
            getCurrentDate(this.formatRequestParameters(appId));
          }
        },
      );
    }
    this.loadApps(false, prevProps);
  }

  componentWillUnmount() {
    this.props.resetCompare();
    this.props.resetChartReport();
  }

  getExportWithParameters = (type) => {
    const { appId, getExport } = this.props;
    const parameters = {
      appId,
      type,
    };
    const { datePicker, periodSwitchValue } = this.state;
    parameters.to = formatRequestDate(datePicker.endDate);
    parameters.from = formatRequestDate(datePicker.startDate);
    parameters.interval = periodSwitchValue;
    getExport(parameters);
  };

  loadApps = (newRequest, prevProps) => {
    const {
      getAppsStatus, getApps, hasMoreApps, pageApps,
    } = this.props;
    const { filters } = this.state;
    const userId = filters[DASHBOARD_FILTERS_NAMES.USER].value;
    if (userId) {
      if (newRequest) {
        getApps({ page: 0, clientId: userId, projection: 'SHORT' });
      } else if (
        prevProps.getAppsStatus !== getAppsStatus &&
        getAppsStatus === SUCCESS &&
        hasMoreApps
      ) {
        getApps({ page: pageApps + 1, clientId: userId, projection: 'SHORT' });
      }
    }
  };

  exportChangeHandler = ({ value }) => {
    this.getExportWithParameters(value.value);
  };

  isNeedGetCompare = (func) => {
    const { filters } = this.state;
    const compareField = filters[DASHBOARD_FILTERS_NAMES.COMPARE].value;
    if (compareField) {
      func();
    }
  };
  transformUserApps = memoize((apps, currentUser) => {
    let resultApps = [];
    if (currentUser && apps.length) {
      resultApps = apps.map(app => ({
        value: app.id,
        label: app.name,
      }));
    }

    const firstElement = [
      {
        label: 'All',
        value: '',
      },
    ];
    return [...firstElement, ...resultApps];
  });

  formatData = memoize((data, compareData) => {
    const { filters } = this.state;
    const valueType = filters[DASHBOARD_FILTERS_NAMES.PARAMETER].value;
    const compareValueType = filters[DASHBOARD_FILTERS_NAMES.COMPARE].value;
    const isCompare = compareData.length;
    const result = data.map((dataTick) => {
      const { date } = dataTick;
      const { value: rawValue } = dataTick;
      const value = formatNumber(rawValue, valueType);
      let compareValue;
      if (isCompare) {
        const compareIndex = compareData
          .findIndex(compareDataTick => compareDataTick.date === date);
        if (compareIndex !== -1) {
          const { value: compareRawValue } = compareData[compareIndex];
          compareValue = formatNumber(compareRawValue, compareValueType);
        }
      }
      return { date, value, compareValue };
    });
    return result;
  });

  transformCampaignsForFilter = memoize((campaigns) => {
    const transformedCampaigns = campaigns.map(campaign => ({
      label: campaign.name,
      value: campaign.id,
    }));
    const firstElement = [
      {
        label: 'All',
        value: '',
      },
    ];
    return [...firstElement, ...transformedCampaigns];
  });

  transformUsersForFilter = memoize((users) => {
    let transformedUsers = [];
    if (users) {
      transformedUsers = users.map(user => ({
        label: user.fullName,
        value: user.id,
      }));
    }

    const firstElement = [
      {
        label: 'All',
        value: '',
      },
    ];
    return [...firstElement, ...transformedUsers];
  });

  formatRequestParameters = (appId, isCompare) => {
    const parameters = {};
    const { datePicker, filters, periodSwitchValue } = this.state;
    const { isOverview, isAdmin, updateDatePicker } = this.props;
    parameters.to = formatRequestDate(datePicker.endDate);
    parameters.from = formatRequestDate(datePicker.startDate);
    parameters.interval = periodSwitchValue;
    parameters.isAdmin = isAdmin;
    if (filters[DASHBOARD_FILTERS_NAMES.CAMPAIGN].value) {
      parameters.campaignId = filters[DASHBOARD_FILTERS_NAMES.CAMPAIGN].value;
    }
    parameters.parameter = isCompare
      ? filters[DASHBOARD_FILTERS_NAMES.COMPARE].value
      : filters[DASHBOARD_FILTERS_NAMES.PARAMETER].value;
    if (isOverview) {
      if (filters[DASHBOARD_FILTERS_NAMES.USER].value) {
        parameters.userId = filters[DASHBOARD_FILTERS_NAMES.USER].value;
        parameters.appId = filters[DASHBOARD_FILTERS_NAMES.APP].value;
      }
    } else {
      parameters.appId = appId;
      updateDatePicker(parameters); // Added date into store
    }
    if (isAdmin && filters[DASHBOARD_FILTERS_NAMES.PLATFORM].value) {
      parameters.platform = filters[DASHBOARD_FILTERS_NAMES.PLATFORM].value;
    }
    return parameters;
  };

  datesChangeHandler = ({ startDate, endDate }) => {
    const datePicker = {
      ...this.state.datePicker,
    };
    datePicker.startDate = startDate;
    datePicker.endDate = endDate;

    const isValidEnd = moment(datePicker.endDate).isValid();
    this.setState(
      () => ({ datePicker }),
      () => {
        if (isValidEnd) {
          const {
            getChartReport, appId, getChartCompareReport, getTableReport, getCurrentDate,
          } = this.props;
          const loc = window.location.pathname.indexOf('overview');
          if (loc > 0) {
            getChartReport(this.formatRequestParameters(appId));
          } else {
            getChartReport(this.formatRequestParameters(appId));
            getTableReport(this.formatRequestParameters(appId));
            getCurrentDate(this.formatRequestParameters(appId));
          }
          this.isNeedGetCompare(() => {
            getChartCompareReport(this.formatRequestParameters(appId, true));
          });
        }
      },
    );
  };

  datesFocusHandler = (focusedDatePickerInput) => {
    this.setState(() => ({ focusedDatePickerInput }));
  };

  datePickerTypeSwitchChangeHandler = (value) => {
    this.setState(() => ({ datePickerTypeSwitchValue: value }));
  };

  periodSwitchChangeHandler = (value) => {
    this.setState(
      () => ({ periodSwitchValue: value }),
      () => {
        const {
          getChartReport, getTableReport, appId, getChartCompareReport, getCurrentDate,
        } = this.props;
        const loc = window.location.pathname.indexOf('overview');
        if (loc > 0) {
          getChartReport(this.formatRequestParameters(appId));
        } else {
          getChartReport(this.formatRequestParameters(appId));
          getTableReport(this.formatRequestParameters(appId));
          getCurrentDate(this.formatRequestParameters(appId));
        }
        this.isNeedGetCompare(() => {
          getChartCompareReport(this.formatRequestParameters(appId, true));
        });
      },
    );
  };

  filterChangeHandler = ({ name, value }) => {
    const filters = {
      ...this.state.filters,
    };
    const { resetCompare } = this.props;
    if (value) {
      filters[name].status = TEXT_FIELD_STATUS.VALID;
    } else {
      filters[name].status = TEXT_FIELD_STATUS.INITIAL;
    }
    let { periodSwitchValue } = this.state;
    const datePicker = {
      ...this.state.datePicker,
    };
    if (
      name === DASHBOARD_FILTERS_NAMES.APP
    ) {
      filters[DASHBOARD_FILTERS_NAMES.CAMPAIGN].value = '';
      filters[DASHBOARD_FILTERS_NAMES.CAMPAIGN].status = TEXT_FIELD_STATUS.INITIAL;
    }
    if (
      name === DASHBOARD_FILTERS_NAMES.USER &&
      value !== filters[DASHBOARD_FILTERS_NAMES.USER].value
    ) {
      Object.keys(filters).forEach((filterName) => {
        if (filterName !== DASHBOARD_FILTERS_NAMES.PARAMETER) {
          filters[filterName].value = '';
          filters[filterName].status = TEXT_FIELD_STATUS.INITIAL;
        } else {
          filters[filterName].value = 'CPI';
          filters[filterName].status = TEXT_FIELD_STATUS.VALID;
        }
      });
      periodSwitchValue = 'DAY';
      resetCompare();
      datePicker.startDate = moment().subtract(1, 'month');
      datePicker.endDate = moment();
    }


    filters[name].value = value.value;
    this.setState(
      () => ({ filters, datePicker, periodSwitchValue }),
      () => {
        const {
          getChartReport,
          appId,
          getChartCompareReport,
          getAppCampaigns,

          isOverview,
        } = this.props;
        if (name !== DASHBOARD_FILTERS_NAMES.COMPARE) {
          getChartReport(this.formatRequestParameters(appId));
        } else if (!value || !value.value) {
          resetCompare();
        }
        if (name === DASHBOARD_FILTERS_NAMES.USER && isOverview) {
          this.loadApps(true, {});
        }
        if (name === DASHBOARD_FILTERS_NAMES.APP && isOverview) {
          getAppCampaigns({
            appId: filters[DASHBOARD_FILTERS_NAMES.APP].value,
          });
        }
        this.isNeedGetCompare(() => {
          getChartCompareReport(this.formatRequestParameters(appId, true));
        });
      },
    );
  };
  handleChangeDayPicker = ({ _d }) => {
    this.datesChangeHandler({ startDate: _d, endDate: _d });
  };

  renderTooltip = ({ payload }) => {
    if (payload && payload[0]) {
      const currentPayload = payload[0];
      const date = formatDateYear(currentPayload.payload.date);
      const { name, value, color } = currentPayload;

      let compareName;
      let compareValue;
      let compareColor;
      if (payload[1]) {
        compareName = payload[1].name;
        compareValue = payload[1].value;
        compareColor = payload[1].color;
      }
      return (
        <div className="custom-tooltip">
          <div className="first-value">
            <div className="date">
              <span>{date}</span>
            </div>
            <div className="value">
              <span>{name}:</span>
              <span
                style={{
                  color,
                }}
              >
                {value}
              </span>
            </div>
          </div>
          {payload[1] && (
            <div className="second-value">
              <div className="value">
                <span>{compareName}:</span>
                <span
                  style={{
                    color: compareColor,
                  }}
                >
                  {compareValue}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  render() {
    const {
      filters,
      datePicker,
      focusedDatePickerInput,
      periodSwitchValue,
      datePickerTypeSwitchValue,
    } = this.state;
    const {
      appCampaigns,
      isAdmin,
      isOverview,
      chartCompareReport,
      chartReport,
      apps,
      users,
    } = this.props;
    const currentUser = filters[DASHBOARD_FILTERS_NAMES.USER].value;
    const currentParameter = filters[DASHBOARD_FILTERS_NAMES.PARAMETER].value;
    const compareParameter = filters[DASHBOARD_FILTERS_NAMES.COMPARE].value;
    const currentViewParameter = PARAMETERS[currentParameter].view;
    const compareViewParameter =
      compareParameter && PARAMETERS[compareParameter].view;
    const transformedUsers = this.transformUsersForFilter(users);
    const transformedApps = this.transformUserApps(apps, currentUser);
    const transformedCampaigns = this.transformCampaignsForFilter(appCampaigns);
    const datePickerWrapperClasses = classNames({
      'date-picker-wrapper': true,
      'is-focused': focusedDatePickerInput,
    });

    const legendPayload = [
      {
        value: currentViewParameter,
        type: 'circle',
        color: '#395ef4',
      },
    ];
    if (compareParameter) {
      legendPayload.push({
        value: compareViewParameter,
        type: 'circle',
        color: '#e6506d',
      });
    }
    const chartData = this.formatData(chartReport, chartCompareReport);
    const showChartPlaceholder = !chartData.length;
    return (
      <div className="chart-part">
        <div className="top-row">
          <ChartFilters
            filters={filters}
            isAdmin={isAdmin}
            isOverview={isOverview}
            changeHandler={this.filterChangeHandler}
            campaigns={transformedCampaigns}
            users={transformedUsers}
            userApps={transformedApps}
          />
          <div className="right-part">
            <div className="top-row">
              <div className={datePickerWrapperClasses}>
                {datePickerTypeSwitchValue === 'RANGE' ? <DateRangePicker
                  startDate={datePicker.startDate}
                  startDateId="id-start-date"
                  endDate={datePicker.endDate}
                  endDateId="id-end-date"
                  isOutsideRange={day => !isInclusivelyBeforeDay(day, moment())}
                  displayFormat="DD MMMM"
                  readOnly
                  customArrowIcon="&mdash;"
                  focusedInput={focusedDatePickerInput}
                  onDatesChange={this.datesChangeHandler}
                  onFocusChange={this.datesFocusHandler}
                  customInputIcon={<img src={arrowIcon} alt="arrow" />}
                /> : <SingleDatePicker
                  date={datePicker.endDate}
                  id="date-picker-day"
                  displayFormat="DD MMMM"
                  readOnly
                  focused={focusedDatePickerInput && focusedDatePickerInput.focused}
                  customArrowIcon="&mdash;"
                  isOutsideRange={day => !isInclusivelyBeforeDay(day, moment())}
                  onDateChange={date => this.datesChangeHandler({ startDate: date, endDate: date })}
                  // onDateChange={date => console.log(date)}
                  onFocusChange={this.datesFocusHandler}
                  customInputIcon={<img src={arrowIcon} alt="arrow" />}
                />}

              </div>
              <div className="switch-type-date-picker">
                <SwitchGroup
                  changeHandler={this.datePickerTypeSwitchChangeHandler}
                  value={datePickerTypeSwitchValue}
                  values={switchDatePickerType}
                />
              </div>
              <div className="export-select-wrapper">
                <CustomMultiSelect
                  placeholder="Export"
                  values={[
                    { label: 'CSV', value: 'CSV' },
                    { label: 'Excel', value: 'EXCEL' },
                  ]}
                  focusHandler={() => {}}
                  changeHandler={this.exportChangeHandler}
                  multi={false}
                />
              </div>
            </div>
            <SwitchGroup
              changeHandler={this.periodSwitchChangeHandler}
              value={periodSwitchValue}
              values={switchGroupValues}
            />
          </div>
        </div>
        <div className="chart-wrapper">
          <ChartPlaceholder isVisible={showChartPlaceholder} />
          <div className="custom-label">
            <span className="label">{currentViewParameter}</span>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              height={250}
              margin={{
                left: 0,
                right: 0,
              }}
              data={chartData}
            >
              <XAxis
                tick={{
                  stroke: '#888888',
                  strokeWidth: '0.5',
                }}
                stroke="#fff"
                padding={{
                  left: 10,
                }}
                style={{
                  transform: 'translateY(19px)',
                }}
                tickFormatter={formatDate}
                minTickGap={15}
                name="Date"
                dataKey="date"
              >
                <Label
                  value="Date"
                  position="insideBottomLeft"
                  style={{
                    ...commonLabelStyle,
                    ...xLabelStyle,
                  }}
                />
              </XAxis>
              <YAxis
                yAxisId="left"
                tickFormatter={value =>
                  formatNumberView(value, currentParameter)
                }
                domain={['dataMin', 'dataMax']}
                tick={{
                  stroke: '#888888',
                  strokeWidth: '0.5',
                }}
                stroke="#fff"
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                offset={20}
                tickFormatter={value =>
                  formatNumberView(value, compareParameter)
                }
                domain={['dataMin', 'dataMax']}
                tick={{
                  stroke: '#888888',
                  strokeWidth: '0.5',
                }}
                stroke="#fff"
              />
              <Tooltip content={this.renderTooltip} />
              <Legend
                verticalAlign="top"
                align="left"
                payload={legendPayload}
                iconSize={10}
                height={36}
              />
              <Line
                type="monotone"
                activeDot={{
                  r: 3,
                  fill: '#fff',
                  stroke: '#395ef4',
                  strokeWidth: 1,
                }}
                connectNulls
                yAxisId="left"
                dot={false}
                legendType="circle"
                name={currentParameter}
                dataKey="value"
                stroke="#395ef4"
                c
              />
              <Line
                yAxisId="right"
                type="monotone"
                activeDot={{
                  r: 3,
                  fill: '#fff',
                  stroke: '#e6506d',
                  strokeWidth: 1,
                }}
                dot={false}
                legendType="circle"
                name={compareParameter}
                dataKey="compareValue"
                stroke="#e6506d"
              />
              <CartesianGrid
                vertical={false}
                stroke="#dedede"
                strokeDasharray="3 3"
                horizontalPoints={[
                  35,
                  55,
                  75,
                  95,
                  115,
                  135,
                  155,
                  175,
                  195,
                  215,
                ]}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="custom-label">
            <span className="label">{compareViewParameter}</span>
          </div>
        </div>
      </div>
    );
  }
}

export default ChartPart;



// WEBPACK FOOTER //
// ./src/containers/MainPanel/components/ChartPart/ChartPart.js